import type { InspectionSlotDto, PropertyListItemDto } from "@unreserved/sdk";
import {
  OPEN_INSPECTION_TIME_SLOTS_PADDED,
  type OpenInspectionTimeSlotOption,
  auStateTimezoneUtils,
  getOpenInspectionTimeSlots,
} from "@unreserved/utils";

export type TimeSlotOption = { id: string; label: string };
export type DateOption = { value: string; label: string; date: Date };

const DEFAULT_LOCALE = "en-AU" as const;

export function getPropertyListingState(
  property: PropertyListItemDto | null | undefined,
): string | null {
  if (!property) return null;
  return typeof property.state === "string" ? property.state : null;
}

export function safeDate(v: unknown): Date | null {
  if (v == null) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function dateKey(d: unknown, timeZone?: string): string {
  const date = safeDate(d);
  if (!date) return "";
  const tz = timeZone ?? auStateTimezoneUtils.getTimezoneForState(null);
  return auStateTimezoneUtils.formatDateKey(date, tz);
}

export function parseDateKey(s: string): Date | null {
  if (!s?.trim()) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (y == null || Number.isNaN(m) || Number.isNaN(d)) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function dateDisplay(d: Date): string {
  const day = d.getDate(),
    m = d.getMonth() + 1,
    y = d.getFullYear() % 100;
  return `${String(day).padStart(2, "0")}/${String(m).padStart(2, "0")}/${String(y).padStart(2, "0")}`;
}

export function formatLocalTime(d: Date, timeZone?: string): string {
  const tz = timeZone ?? auStateTimezoneUtils.getTimezoneForState(null);
  return d.toLocaleTimeString(DEFAULT_LOCALE, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  });
}

export function buildDatesAndSlots(
  openInspections: InspectionSlotDto[],
  timeZone?: string,
): {
  dateOptions: DateOption[];
  slotsByDate: Map<string, TimeSlotOption[]>;
} {
  const tz = timeZone ?? auStateTimezoneUtils.getTimezoneForState(null);
  const slotsByDate = new Map<string, TimeSlotOption[]>();
  for (const i of openInspections) {
    const start = safeDate(i.inspectionStartTime);
    if (!start) continue;
    const key = dateKey(start, tz);
    if (!key) continue;
    const end = safeDate(i.inspectionEndTime) ?? start;
    const list = slotsByDate.get(key) ?? [];
    list.push({ id: i.id, label: `${formatLocalTime(start, tz)} - ${formatLocalTime(end, tz)}` });
    slotsByDate.set(key, list);
  }
  const keys = [...slotsByDate.keys()].sort(
    (a, b) => (parseDateKey(a)?.getTime() ?? 0) - (parseDateKey(b)?.getTime() ?? 0),
  );
  const dateOptions: DateOption[] = keys
    .map((k) => {
      const d = parseDateKey(k);
      return d ? { value: k, label: dateDisplay(d), date: d } : null;
    })
    .filter((o): o is DateOption => o != null);
  return { dateOptions, slotsByDate };
}

export function syncToFirst(
  dateOptions: DateOption[],
  slotsByDate: Map<string, TimeSlotOption[]>,
  setValue: (name: "selectedDate" | "selectedTime", value: Date | string) => void,
  setSelectedSlot: (id: string | null) => void,
) {
  if (dateOptions.length === 0) return;
  const first = dateOptions[0];
  const slots = slotsByDate.get(first.value) ?? [];
  setValue("selectedDate", first.date);
  setValue("selectedTime", slots[0]?.id ?? "");
  setSelectedSlot(slots[0]?.id ?? null);
}

/** Half-hour time slots from 9 AM to 9 PM. Prefer OPEN_INSPECTION_TIME_SLOTS_PADDED. */
export function buildTimeSlots(): TimeSlotOption[] {
  return getOpenInspectionTimeSlots({ labelStyle: "padded" }).map(
    ({ id, label }: OpenInspectionTimeSlotOption) => ({ id, label }),
  );
}

export const STATIC_TIME_SLOTS: TimeSlotOption[] = OPEN_INSPECTION_TIME_SLOTS_PADDED.map(
  ({ id, label }) => ({ id, label }),
);
